export interface Holding {
  symbol: string;
  name?: string;
  shares: number;
  avgCost: number;
  weight: number;
  targetWeight: number;
  market: 'US' | 'KR';
  value?: number;
  displayValue?: number;
  displayPrice?: number;
  displayAvgCost?: number;
  displayCurrency?: 'US' | 'KR';
  displayPriceCurrency?: 'US' | 'KR';
  marketValue?: number;
  currentPrice?: number;
  account_id?: string;
  is_sandbox?: boolean;
  is_paper?: number | boolean;
  paper_mode?: 'VIRTUAL_SANDBOX' | 'BROKER_PAPER' | null;
  distribution?: { accountId: string; shares: number; avgCost: number }[];
}

export type MarketRegime = 'Goldilocks' | 'Reflation' | 'Stagflation' | 'Deflation' | 'Neutral';

export interface MacroState {
  asOfDate: string;
  regimes: {
    liquidity: 'Tightening' | 'Neutral' | 'Easing';
    credit: 'Contracting' | 'Flat' | 'Expanding' | 'Recovery' | 'Growth';
    policy: 'Normal' | 'Blackout' | 'Pre-FOMC' | 'Post-FOMC' | 'Restrictive' | 'Expansive' | 'Hawkish' | 'Dovish' | 'Wait & See' | 'Pivot';
  };
  riskMetrics: {
    riskScore: number;
    macroWeight: number;
    portfolioRiskBudgetPct: number;
    trimThresholdPct: number;
    updatedAt: string;
    riskScoreChange?: number;
    macroWeightChange?: number;
    portfolioRiskBudgetPctChange?: number;
    trimThresholdPctChange?: number;
  };
  marketRegime?: MarketRegime;
  aiSummary: string;
  aiIntelligence?: string;
  aiStrategies?: string[];
  aiAnalysisDate?: string;
  aiAnalysisError?: string;
  aiIntelligenceError?: string;
  aiIntelligenceDate?: string;
  riskScore?: number;
  
  // Macro Event Impacts (Quant Signal Radar)
  event_type?: string;
  actual_value?: number;
  consensus_value?: number;
  prior_regime?: MarketRegime;
  new_regime?: MarketRegime;
  ai_reasoning?: string;
  benefited_tickers?: string[];
  harmed_tickers?: string[];

  // New indicators included in state
  fedFunds: { value: number; date: string; change?: number };
  inflation: { value: number; date: string; change?: number };
  unemployment: { value: number; date: string; change?: number };
  government?: {
    treasury10Y: { value: number; date: string; change: number };
    treasury5Y?: { value: number; date: string; change: number };
    treasury2Y: { value: number; date: string; change: number };
    treasury3M?: { value: number; date: string; change: number };
    yieldSpread10Y3M?: { value: number; date: string; change: number };
    highYieldSpread?: { value: number; date: string; change: number };
    tgaBalance: { value: number; date: string; change: number };
    fedBalanceSheet: { value: number; date: string; change: number };
    reverseRepo: { value: number; date: string; change: number };
    fedReserves?: { value: number; date: string; change: number };
    m2MoneySupply?: { value: number; date: string; change: number };
    m2?: { value: number; date: string; change: number };
    velocityOfM2?: { value: number; date: string; change: number };
    netLiquidity?: { value: number; date: string; change?: number };
    bokRate?: { value: number; date: string };
    krM2?: { value: number; date: string };
    krCpi?: { value: number; date: string };
    krCreditSpread?: { value: number; date: string };
    krYieldSpread?: { value: number; date: string };
    vkospi?: { value: number; date: string; change: number };
    krExports?: { value: number; date: string };
    krCpRate?: { value: number; date: string; change?: number };
    krBsi?: { value: number; date: string; change?: number };
    krExportPrice?: { value: number; date: string; change?: number };
    krImportPrice?: { value: number; date: string; change?: number };
    krPpi?: { value: number; date: string; change?: number };
    krCds?: { value: number; date: string; change?: number };
    krFuturesNetBuy?: { value: number; date: string; change?: number };
    krCustomerDeposit?: { value: number; date: string; change?: number };
    krMarginBalance?: { value: number; date: string; change?: number };
    realM2?: { value: number; date: string; change?: number };
  };
  futures?: {
    sp500: { value: number; date: string; change: number };
    nasdaq: { value: number; date: string; change: number };
    dow: { value: number; date: string; change: number };
    russell: { value: number; date: string; change: number };
    treasury10y: { value: number; date: string; change: number };
    vix: { value: number; date: string; change: number };
    krx200?: { value: number; date: string; change: number };
    sox?: { value: number; date: string; change: number };
    brent?: { value: number; date: string; change: number };
  };
  economics?: {
    coreCpi: { value: number; date: string; change: number };
    coreRetailSales?: { value: number; date: string; change: number; releaseDate?: string };
    corePce: { value: number; date: string; change: number };
    ppi: { value: number; date: string; change: number };
    retailSales: { value: number; date: string; change: number };
    payrolls: { value: number; date: string; change: number };
    initialClaims: { value: number; date: string; change: number };
    pmiManufacturing?: { value: number; date: string; change: number };
    pmiServices?: { value: number; date: string; change: number };
  };
  calendars?: MacroCalendar | null;
}


export interface CalendarEvent {
  date: string;
  event: string;
  impact: string;
  previous?: string | number;
  estimate?: string | number;
  actual?: string | number;
  surprise?: string | number;
  country: string;
}

export interface EarningsEvent {
  date: string;
  symbol: string;
  hour: string;
  epsEstimate?: number;
  revenueEstimate?: number;
}

export interface MacroCalendar {
  economic: CalendarEvent[];
  earnings: EarningsEvent[];
}

export type ShockLevel = 'NORMAL' | 'WATCH' | 'WARNING' | 'CRITICAL';
export type PortfolioGuidance = 'HOLD' | 'HEDGE_REQUIRED' | 'REDUCE_BETA' | 'CASH_BUFFER' | 'BUY_THE_DIP';

export interface MacroEventSignal {
  id: string;
  event_type: 'CPI' | 'PCE' | 'FOMC' | 'NFP' | 'GDP' | 'PPI' | string;
  market: 'US' | 'KR';
  release_date: string;
  release_time?: string;
  actual_value?: number | null;
  consensus_value?: number | null;
  previous_value?: number | null;
  surprise_delta?: number | null;
  surprise_pct?: number | null;
  shock_level: ShockLevel;
  affected_sectors?: string[];
  portfolio_guidance: PortfolioGuidance;
  market_reaction?: {
    nasdaq_futures?: number;
    sp500_futures?: number;
    vix?: number;
    us10y?: number;
    description?: string;
  };
  summary: string;
  created_at?: string;
  updated_at?: string;
}

export interface CentralBankRateItem {
  id: string;
  bankName: string;
  rateType: 'RANGE' | 'SINGLE';
  display: string;
  targetRateLower?: number | null;
  targetRateUpper?: number | null;
  targetRateSingle?: number | null;
  changeBp: number;
  action: 'HIKE' | 'HOLD' | 'CUT';
  decisionDate: string;
  effectiveDate: string;
  nextMeetingDate?: string;
  statementSummary?: string;
  source: string;
  updatedAt?: string;
}

export interface PolicyRatesData {
  fed: CentralBankRateItem;
  bok: CentralBankRateItem;
}

export interface MacroIndicators {
  policyRates?: PolicyRatesData;
  fedFunds: { value: number; date: string; releaseDate?: string; change?: number };
  inflation: { value: number; date: string; releaseDate?: string; change?: number };
  unemployment: { value: number; date: string; releaseDate?: string; change?: number };
  m2MoneySupply: { value: number; date: string; change: number };
  m2?: { value: number; date: string; change: number }; // Added
  sp500: { value: number; date: string; change: number };
  nasdaq: { value: number; date: string; change: number };
  sox?: { value: number; date: string; change: number };
  kospi?: { value: number; date: string; change: number };
  kosdaq?: { value: number; date: string; change: number };
  vix: { value: number; date: string; change: number };
  vixeq?: { value: number; date: string; change: number };
  fearGreed?: { value: number; date: string; change: number; rating?: string };
  kospiFgi?: { value: number; date: string; change: number; rating?: string };
  dxy: { value: number; date: string; change: number };
  usdkrw?: { value: number; date: string; change: number };
  yieldSpread: { value: number; date: string; change: number };
  vkospi?: { value: number; date: string; change: number };
  bitcoin: { value: number; date: string; change: number };
  gold: { value: number; date: string; change: number };
  silver: { value: number; date: string; change: number };
  copper: { value: number; date: string; change: number };
  oil: { value: number; date: string; change: number };
  naturalGas: { value: number; date: string; change: number };
  ironOre?: { value: number; date: string; change: number };
  lithium?: { value: number; date: string; change: number };
  nickel?: { value: number; date: string; change: number };
  cobalt?: { value: number; date: string; change: number };
  wheat?: { value: number; date: string; change: number };
  corn?: { value: number; date: string; change: number };
  soybeans?: { value: number; date: string; change: number };
  bdi?: { value: number; date: string; change: number };
  russell2000: { value: number; date: string; change: number };
  djia: { value: number; date: string; change: number };
  sectors?: Record<string, { value: number; change: number; name: string }>;
  government?: {
    treasury5Y?: { value: number; date: string; change: number };
    treasury10Y: { value: number; date: string; change: number };
    treasury30Y?: { value: number; date: string; change: number };
    treasury2Y: { value: number; date: string; change: number };
    treasury3M?: { value: number; date: string; change: number };
    krTreasury2Y?: { value: number; date: string; change: number };
    krTreasury3Y?: { value: number; date: string; change: number };
    krTreasury5Y?: { value: number; date: string; change: number };
    krTreasury10Y?: { value: number; date: string; change: number };
    krTreasury20Y?: { value: number; date: string; change: number };
    krTreasury30Y?: { value: number; date: string; change: number };
    yieldSpread10Y3M?: { value: number; date: string; change: number };
    highYieldSpread?: { value: number; date: string; change: number };
    tgaBalance: { value: number; date: string; change: number };
    fedBalanceSheet: { value: number; date: string; change: number };
    reverseRepo: { value: number; date: string; change: number };
    fedReserves?: { value: number; date: string; change: number };
    m2MoneySupply?: { value: number; date: string; change: number }; // Made optional
    m2?: { value: number; date: string; change: number };
    velocityOfM2?: { value: number; date: string; change: number };
    netLiquidity?: { value: number; date: string; change?: number };
    bokRate?: { value: number; date: string; change?: number };
    krM2?: { value: number; date: string; change?: number };
    krYieldSpread?: { value: number; date: string; change?: number };
    vkospi?: { value: number; date: string; change: number };
    krCpi?: { value: number; date: string; change?: number };
    krCreditSpread?: { value: number; date: string; change?: number };
    krExports?: { value: number; date: string; change?: number };
    krCpRate?: { value: number; date: string; change?: number };
    krBsi?: { value: number; date: string; change?: number };
    krExportPrice?: { value: number; date: string; change?: number };
    krImportPrice?: { value: number; date: string; change?: number };
    krPpi?: { value: number; date: string; change?: number };
    krCds?: { value: number; date: string; change?: number };
    krFuturesNetBuy?: { value: number; date: string; change?: number };
    krCustomerDeposit?: { value: number; date: string; change?: number };
    krMarginBalance?: { value: number; date: string; change?: number };
    realM2?: { value: number; date: string; change?: number };
  };
  futures?: {
    sp500: { value: number; date: string; change: number };
    nasdaq: { value: number; date: string; change: number };
    dow: { value: number; date: string; change: number };
    russell: { value: number; date: string; change: number };
    treasury10y: { value: number; date: string; change: number };
    vix: { value: number; date: string; change: number };
    krx200?: { value: number; date: string; change: number };
    sox?: { value: number; date: string; change: number };
    brent?: { value: number; date: string; change: number };
  };
  economics?: {
    coreCpi: { value: number; date: string; change: number };
    coreRetailSales?: { value: number; date: string; change: number; releaseDate?: string };
    corePce: { value: number; date: string; change: number };
    ppi: { value: number; date: string; change: number };
    retailSales: { value: number; date: string; change: number };
    payrolls: { value: number; date: string; change: number };
    initialClaims: { value: number; date: string; change: number };
    pmiManufacturing?: { value: number; date: string; change: number };
    pmiServices?: { value: number; date: string; change: number };
  };
  calendars?: MacroCalendar | null;
  isMock: boolean;
}

export interface MarketSignal {
  symbol: string;
  name?: string;
  price: number;
  prevClose: number;
  pctChange: number;
  shortRatio: number;
  daysToCover: number;
  putCallRatio: number;
  ivRank: number | null;
  gammaFlip: number;
  maxPain: number;
  totalScore: number;
  alertLevel: 'NORMAL' | 'WATCH' | 'HIGH' | 'ELEVATED' | 'CRITICAL';
  portfolioAction: 'HOLD' | 'BUY' | 'SELL' | 'EXIT' | 'ADD' | 'REDUCE' | 'WATCH';
  marketState: 'OPEN' | 'CLOSED' | 'PRE' | 'POST' | 'REGULAR';
  market?: 'US' | 'KR';
  timestamp?: string;
  isPending?: boolean;
  buyTargetPrice?: number;
  sellTargetPrice?: number;
  proposedTradeShares?: number;
  rvol?: number;
  volumeScore?: number;
  cmf?: number;
  volumeSignalType?: 'SPIKE_BREAKOUT' | 'ACCUMULATION' | 'TURNOVER_SURGE' | 'NORMAL';
  avgVolume?: number;
}

export interface QuantRecommendation {
  id: string;
  symbol: string;
  name: string;
  market: 'US' | 'KR';
  sector?: string;
  regime_suitability?: any;
  momentum_score: number;
  quality_score: number;
  value_score: number;
  volatility_score: number;
  volume_score: number;
  overall_score: number;
  rvol: number;
  cmf_20: number;
  volume_signal_type: 'SPIKE_BREAKOUT' | 'ACCUMULATION' | 'TURNOVER_SURGE' | 'NORMAL';
  avg_volume_20d: number;
  fair_price?: number;
  fair_price_ratio?: number;
  target_buy_price?: number;
  target_sell_price?: number;
  stop_loss_price?: number;
  atr_rate?: number;
  current_price?: number;
  price_session?: string;
  price_updated_at?: string;
  ai_entry_strategy?: string;
  updated_at: string;
}

export interface TrendDataPoint {
  date: string;
  value: number;
  [key: string]: any;
}

/** Response shape returned by /api/trend/{indicator} */
export interface TrendApiResponse {
  /** Monthly data points in chronological order */
  data: TrendDataPoint[];
  /** true when the server returned an error and mock data is substituted client-side */
  isMock?: boolean;
  /** ISO timestamp when data was written to KV cache */
  cachedAt?: string;
}

export interface OptimizationData {
  efficientFrontier: { risk: number; return: number }[];
  optimalWeights: Record<string, number>;
  originalWeights: Record<string, number>;
  metrics: {
    expectedReturn: number;
    expectedVolatility: number;
    sharpeRatio: number;
  };
  rationale: string;
}

export interface NewsItem {
  id: string;
  title: string;
  content: string;
  url: string;
  source: string;
  publishedAt: string;
  relatedSymbols: string[];
  category: 'earnings' | 'macro' | 'geopolitical' | 'sector' | 'general';
  urgency: 'breaking' | 'major' | 'normal';
}

export interface NewsImpactEntry {
  symbol: string;
  name?: string;
  direction: 'bullish' | 'bearish' | 'neutral';
  magnitude: number; // -100 ~ +100
  rationale: string;
  recommendedAction: 'BUY' | 'SELL' | 'HOLD' | 'WATCH' | 'REDUCE';
  isHolding: boolean;
  isWatchlist: boolean;
}

export interface NewsImpactResult {
  newsId: string;
  impactMap: NewsImpactEntry[];
  positionStrategy: string;
  analyzedAt: string;
}

// ─── Web Traffic Alternative Data & News Cross-Signal Types ─────────────────

export type TrafficSignalType =
  | 'CONFIRMED_BREAKOUT'     // 🔥 속보 호재 + 웹 트래픽 급증 (실적 직결 검증)
  | 'STEALTH_ACCUMULATION'   // 💎 뉴스 잠잠 + 웹 트래픽 이상 급증 (어닝 서프라이즈 선취매)
  | 'HYPE_TRAP_WARNING'      // ⚠️ 자극적 호재 속보 + 웹 트래픽 역성장/무반응 (과열·설거지 경고)
  | 'OVERSOLD_CONTRARIAN';   // 🛡️ 악재 속보 급락 + 웹 트래픽 견조 (펀더멘털 이상 무, 저점 매수)

export interface DomainTrafficMetricPoint {
  recordedDate: string;
  visitsEst: number;
  wikiViews: number;
  techBuzzCount: number;
  domainRank?: number | null;
  dodGrowthPct: number;
  wowGrowthPct: number;
  momGrowthPct: number;
  zScore: number;
  trafficMomentumScore: number;
}

export interface CompanyTrafficOverview {
  symbol: string;
  market: 'US' | 'KR';
  companyNameKo: string;
  companyNameEn: string;
  primaryDomain: string;
  subDomains: string[];
  sector: string;
  trafficSensitivityWeight: number;
  latestMetric: DomainTrafficMetricPoint | null;
  history: DomainTrafficMetricPoint[];
}

export interface NewsTrafficCrossSignal {
  id: string;
  symbol: string;
  market: 'US' | 'KR';
  companyName: string;
  companyNameEn?: string;
  primaryDomain: string;
  sector?: string;
  signalType: TrafficSignalType;
  trafficMomentumScore: number;   // 0 ~ 100
  trafficWowGrowthPct: number;    // 주간 트래픽 증감률 (%)
  trafficZScore: number;          // 30일 표준편차 Z-Score
  visitsEst?: number;             // 일간 추정 트래픽 지수
  wikiViews?: number;             // 위키피디아 페이지뷰 (7일 합산/일평균)
  techBuzzCount?: number;         // 글로벌/커뮤니티 언급 버즈량
  newsId?: string | null;
  newsTitle?: string | null;
  newsUrl?: string | null;
  newsPublishedAt?: string | null;
  newsUrgency?: 'breaking' | 'major' | 'normal';
  newsSentimentScore: number;     // -1.0 ~ +1.0
  predictedImpactPct: number;     // 예상 주가 영향도 (%)
  recommendedAction: 'BUY' | 'ADD' | 'WATCH' | 'REDUCE' | 'SELL' | 'HOLD';
  confidenceScore: number;        // 0 ~ 100
  aiRationaleKo: string;
  aiRationaleEn: string;
  historySeries?: { date: string; trafficIndex: number; wikiViews: number }[];
  updatedAt: string;
}

export interface WatchlistItem {
  symbol: string;
  name?: string;
  market: 'US' | 'KR';
  added_at: string;
  notes?: string;
}

// ─── Membership Tier System ────────────────────────────────────────────────────────────────

export type MembershipTier = 'TIER1' | 'TIER2' | 'TIER3' | 'TIER4' | 'TIER5';

export interface TierLimits {
  /** 보유 종목 최대 (-1 = 무제한) */
  holdingsLimit: number;
  /** 관심종목 최대 (-1 = 무제한) */
  watchlistLimit: number;
  /** 뉴스 AI 분석 월 한도 (-1 = 무제한) */
  newsAnalysisPerMonth: number;
  /** 포트폴리오 AI 조언 월 한도 (-1 = 무제한) */
  portfolioAdvicePerMonth: number;
  /** 종목 기술적 분석 월 한도 (-1 = 무제한) */
  stockAnalysisPerMonth: number;
  /** 포트폴리오 최적화 월 한도 (-1 = 무제한) */
  optimizationPerMonth: number;
  /** 내역 보존 일수 (-1 = 무제한) */
  historyDays: number;
  /** 최대 연동 계좌 수 (0 = 불가) */
  maxAccounts: number;
  /** CSV 내보내기 */
  canExportCSV: boolean;
  /** PDF 리포트 생성 */
  canExportPDF: boolean;
  /** 모든 AI 모델 선택 가능 */
  canUseAllModels: boolean;
  /** 푸시 알림 */
  canReceivePushAlerts: boolean;
  /** 속보/Breaking 즉시 알림 */
  canReceiveBreakingAlerts: boolean;
  /** 사용자 지정 AI 프롬프트 */
  canCustomizePrompts: boolean;
  /** 증권사 자동 연동 */
  canConnectBrokerage: boolean;
  /** API 외부 접근 */
  canAccessApi: boolean;
  /** 수동/직접 보유 자산 추가 허용 */
  canAddHoldings: boolean;
}

export const TIER_LIMITS: Record<MembershipTier, TierLimits> = {
  TIER1: {
    holdingsLimit: 5,
    watchlistLimit: 10,
    newsAnalysisPerMonth: 5,
    portfolioAdvicePerMonth: 0,
    stockAnalysisPerMonth: 0,
    optimizationPerMonth: 0,
    historyDays: 30,
    maxAccounts: 0,
    canExportCSV: false,
    canExportPDF: false,
    canUseAllModels: false,
    canReceivePushAlerts: false,
    canReceiveBreakingAlerts: false,
    canCustomizePrompts: false,
    canConnectBrokerage: false,
    canAccessApi: false,
    canAddHoldings: true,
  },
  TIER2: {
    holdingsLimit: 15,
    watchlistLimit: 30,
    newsAnalysisPerMonth: 20,
    portfolioAdvicePerMonth: 3,
    stockAnalysisPerMonth: 5,
    optimizationPerMonth: 0,
    historyDays: 90,
    maxAccounts: 1,
    canExportCSV: true,
    canExportPDF: false,
    canUseAllModels: false,
    canReceivePushAlerts: true,
    canReceiveBreakingAlerts: true,
    canCustomizePrompts: false,
    canConnectBrokerage: true,
    canAccessApi: false,
    canAddHoldings: true,
  },
  TIER3: {
    holdingsLimit: 30,
    watchlistLimit: 50,
    newsAnalysisPerMonth: 50,
    portfolioAdvicePerMonth: 10,
    stockAnalysisPerMonth: 20,
    optimizationPerMonth: 3,
    historyDays: 365,
    maxAccounts: 3,
    canExportCSV: true,
    canExportPDF: false,
    canUseAllModels: false,
    canReceivePushAlerts: true,
    canReceiveBreakingAlerts: true,
    canCustomizePrompts: false,
    canConnectBrokerage: true,
    canAccessApi: false,
    canAddHoldings: true,
  },
  TIER4: {
    holdingsLimit: 100,
    watchlistLimit: 200,
    newsAnalysisPerMonth: 150,
    portfolioAdvicePerMonth: 30,
    stockAnalysisPerMonth: 60,
    optimizationPerMonth: 10,
    historyDays: 1095,
    maxAccounts: 5,
    canExportCSV: true,
    canExportPDF: true,
    canUseAllModels: false,
    canReceivePushAlerts: true,
    canReceiveBreakingAlerts: true,
    canCustomizePrompts: false,
    canConnectBrokerage: true,
    canAccessApi: false,
    canAddHoldings: true,
  },
  TIER5: {
    holdingsLimit: -1,
    watchlistLimit: -1,
    newsAnalysisPerMonth: -1,
    portfolioAdvicePerMonth: -1,
    stockAnalysisPerMonth: -1,
    optimizationPerMonth: -1,
    historyDays: -1,
    maxAccounts: -1,
    canExportCSV: true,
    canExportPDF: true,
    canUseAllModels: true,
    canReceivePushAlerts: true,
    canReceiveBreakingAlerts: true,
    canCustomizePrompts: true,
    canConnectBrokerage: true,
    canAccessApi: true,
    canAddHoldings: true,
  },
};

/** TIER_LIMITS에서 숫자형 한도를 읽어 초과 여부를 검사 */
export function isWithinLimit(limit: number, current: number): boolean {
  if (limit === -1) return true; // 무제한
  return current < limit;
}

/** 등급 간 접근 가능 여부 (current >= required) */
export function canAccessTier(current: string, required: string): boolean {
  const normalize = (t: string) => {
    if (t === 'FREE') return 'TIER1';
    if (t === 'PRO') return 'TIER3';
    if (t === 'PREMIUM') return 'TIER5';
    return t;
  };
  const normCurrent = normalize(current);
  const normRequired = normalize(required);
  const order = ['TIER1', 'TIER2', 'TIER3', 'TIER4', 'TIER5'];
  return order.indexOf(normCurrent) >= order.indexOf(normRequired);
}

/** 사용자 프로파일 (API 응답 형태) */
export interface UserProfile {
  userId?: string;
  notificationsEnabled: boolean;
  uiLanguage: string;
  analysisLanguage: string;
  targetGoalAmount: number;
  targetGoalAmountUS: number;
  targetGoalAmountKR: number;
  targetMarket: string;
  investorType: number;
  investorExperienceTier?: number;
  investmentYears?: number;
  activeSandboxMode?: boolean;
  realTradingWhitelist?: boolean;
  realTradingApprovedAt?: string | null;
  serviceMode?: 'SIMULATION' | 'LIVE';
  globalServiceMode?: 'SIMULATION' | 'LIVE_HYBRID' | 'LIVE_PUBLIC';
  canAccessLiveTrading?: boolean;
  isHostRestricted?: boolean;
  membershipTier: MembershipTier;
  tierExpiresAt?: string;
  // Legacy addon properties migrated to activeAddons
  // activeAddons: { id: string, expiresAt: string | null }[];
  autonomousAgentTokenBudget?: number;
  autonomousAgentTokenUsed?: number;
  autonomousAgentTier?: string;
  agentHitlPreference?: 'hitl' | 'auto';
  experienceScore?: number;
  aiUsageCount?: number;
  aiUsageDetails?: Record<string, number>;
  aiUsageSummary?: {
    tier: string;
    monthlyUsedTokens: number;
    monthlyTokenQuota: number;
    dailyUsedRequests: number;
    dailyRequestLimit: number;
    remainingTokens: number;
    remainingDailyRequests: number;
    tokenUsagePercent: number;
    tokensSavedByCache?: number;
  };
  isApproved: boolean;
  role: string;
  email?: string;
  displayName?: string;
  phoneNumber?: string;
  rebalanceInterval: string;
  hasBillingKey?: boolean;
  cardCompany?: string | null;
  cardNumber?: string | null;
  billingCycle?: 'monthly' | 'annual';
  nextBillingCycle?: 'monthly' | 'annual' | null;
  nextMembershipTier?: MembershipTier | null;
  /** 본인(실명)인증 완료 여부 */
  identityVerified?: boolean;
  /** 본인인증 완료 일시 (ISO 8601) */
  identityVerifiedAt?: string | null;
  /** 인증된 실명 (마스킹 저장: 홍*동) */
  verifiedName?: string | null;
  /** 2단계 인증(2FA/TOTP) 활성화 여부 */
  otpEnabled?: boolean;
  /** 2단계 인증 등록/활성화 일시 */
  otpVerifiedAt?: string | null;
  /** PG 가맹점 심사 및 모의투자 SaaS 이용약관 동의 여부 */
  hasAgreedSimulationTerms?: boolean;
  /** 모의투자 SaaS 이용약관 동의 일시 */
  simulationTermsAgreedAt?: string | null;
  /** API 접근 부가상품 */
  /** 통화 설정 */
  currencyPreference?: 'USD' | 'KRW';
  /** 화면별 Collapsible UI 접힘/펼침 설정 저장 */
  uiCollapsedPreferences?: Record<string, any>;
  /** 알림 임계값 및 5대 정책 플래그 */
  vixThreshold?: number;
  mddThreshold?: number;
  bondYieldThreshold?: number;
  usdKrwResistance?: number;
  usdKrwSupport?: number;
  stopLossThreshold?: number;
  concentrationThreshold?: number;
  yieldCurveAlertEnabled?: boolean;
  oilSpikeThreshold?: number;
  fomcAlertEnabled?: boolean;
  vixRecoveryAlertEnabled?: boolean;
  aiProposalAlertEnabled?: boolean;
  executionAlertEnabled?: boolean;
  billingAlertEnabled?: boolean;
  /** 에이전트 부가상품 플래그 (레거시 호환) */
  /** 구독 중인 부가상품 목록 (레거시 호환) */
  activeAddons?: { id: string, expiresAt: string | null }[];
  subscribedAddons?: { id: string, expiresAt: string | null }[];
}

// ─── Signal & Simulation Execution Log & Performance ────────────────────────────────

/** 시그널 및 시뮬레이션 실행 로그 단건 */
export interface AutoTradeLog {
  id: string;
  userId?: string;
  symbol: string;
  name?: string;
  market: 'US' | 'KR';
  action: 'BUY' | 'SELL' | 'HOLD' | 'EXIT' | 'PARTIAL' | 'ADJUST';
  triggerType: 'MACRO_SIGNAL' | 'REBALANCING' | 'STOP_LOSS' | 'MANUAL' | 'AI_RECOMMENDATION' | 'AGENT_HITL_APPROVED' | 'AGENT_WAKEUP_EXECUTED' | 'AGENT_MANUAL_RESUME_EXECUTED' | string;
  shares: number;
  executionPrice: number;
  targetPrice?: number;
  totalAmount: number;
  currency: 'USD' | 'KRW';
  status: 'FILLED' | 'PARTIAL' | 'CANCELLED' | 'FAILED' | 'SIMULATED' | 'PENDING';
  executionMode?: 'LIVE' | 'PAPER';
  executedAt: string;
  pnl?: number;         // 실현손익 (원화/달러)
  pnlPct?: number;      // 실현손익률 (%)
  macroRegime?: string; // 당시 매크로 레짐 (예: Goldilocks)
  riskScore?: number;   // 당시 리스크 스코어 (0–100)
  confluenceScore?: number; // 당시 에이전트 확신도 (0-100)
  broker?: string;      // 체결/할당 증권사 (kis | ls | kiwoom | toss)
  accountId?: string;   // 대상 계좌 ID
  feeAmount?: number;   // 발생 위탁수수료 + 제비용
  taxAmount?: number;   // 증권거래세 / SEC Fee / TAF
  netAmount?: number;   // 실정산액
  feeBreakdown?: any;   // 수수료 세부 명세
  notes?: string;
}

/** 시그널 및 시뮬레이션 성과 집계 지표 */
export interface AutoTradingPerformanceSummary {
  totalTrades: number;
  winRate: number;             // 승률 %
  totalRealizedPnl: number;   // 총 실현손익
  totalRealizedPnlPct: number; // 총 실현손익률 %
  avgHoldingDays: number;      // 평균 보유일
  bestTrade: AutoTradeLog | null;
  worstTrade: AutoTradeLog | null;
  sharpeRatio: number;
  maxDrawdown: number;         // 최대낙폭(MDD) %
  byTrigger: Record<string, number>; // 트리거 유형별 거래 건수
  bySymbol: { symbol: string; name?: string; count: number; pnl: number }[];
  monthlyPnl: { month: string; pnl: number; pnlPct: number; trades: number }[];
  cumulativePnlSeries: { date: string; pnl: number; label: string }[];
}

// ─── Autonomous Agent Data Models ───────────────────────────────────────────

export type AgentTaskType = 'REBALANCE' | 'MACRO_ALERT' | 'DAILY_REPORT' | 'SCREENER' | 'TACTICAL_BUY' | 'FX_HEDGE' | 'RISK_DEFENSE' | 'PROFIT_TAKING' | 'PORTFOLIO_GUARD' | 'MACRO_EVENT_HEDGE';
export type AgentTaskStatus = 'PENDING' | 'PLANNING' | 'EXECUTING' | 'WAITING_FOR_APPROVAL' | 'WAITING_FOR_MARKET_OPEN' | 'INSUFFICIENT_CASH_HOLD' | 'COMPLETED' | 'FAILED' | 'CANCELLED' | 'EXECUTION_FAILED' | 'PARTIALLY_EXECUTED' | 'EXPIRED';

export interface ProposedAction {
  symbol: string;
  name?: string;
  market?: 'US' | 'KR';
  isTradingAllowed?: boolean;
  marketSession?: string;
  pricingSession?: string;
  quoteSourcePrice?: number;
  bidAskSpreadPct?: number;
  action: 'BUY' | 'SELL' | 'HOLD';
  shares: number;
  price?: number;
  orderPrice?: number;
  currentPrice?: number;
  avgCost?: number;
  expectedPnl?: number;
  expectedPnlPct?: number;
  targetPrice?: number;
  stopLossPrice?: number;
  riskRewardRatio?: string;
  reason?: string;
  assignedBroker?: 'kis' | 'ls' | string;
  assignedAccountId?: string;
  assignedAccountNum?: string;
  assignedAccountName?: string;
  allocationType?: 'SELL_HOLDING' | 'ADD_BUY_HOLDING' | 'NEW_BUY_ALLOC';
  allocationReason?: string;
  brokerStatus?: string;
  maintenanceWindow?: string;
  canReserve?: boolean;
  isExecutable?: boolean;
  holdReason?: 'INSUFFICIENT_CASH' | 'MAINTENANCE' | 'NONE' | string;
  requiredAmount?: number;
  grossAmount?: number;
  estimatedFee?: number;
  netSettlementAmount?: number;
  feeBreakdown?: any;
  shortageAmount?: number;
  execution?: {
    success?: boolean;
    price?: number;
    avgCost?: number;
    expectedPnl?: number;
    expectedPnlPct?: number;
    quantity?: number;
    timestamp?: string;
    message?: string;
    orderId?: string | null;
    mode?: 'PAPER' | 'LIVE' | 'WAITING_KEY' | string;
    broker?: string;
    reason?: string;
    error?: string;
    status?: string;
    rawResponse?: any;
  };
}

export interface ReasoningStep {
  stepNumber: number;
  title: string;
  description: string;
}

export interface AgentStrategyProposal {
  id: string;
  taskId: string;
  userId: string;
  confluenceScore: number;
  triggerSource?: string;
  macroEvidence?: {
    summary: string;
    regime: string;
    vix: number;
    sentiment?: string;
  } | null;
  quantEvidence?: {
    summary: string;
    topFactors: string[];
    averageScore: number;
  } | null;
  optionsFlowEvidence?: {
    summary: string;
    gammaStatus?: string;
    putCallRatio?: number;
    ivCondition?: string;
  } | null;
  newsSentimentEvidence?: {
    summary: string;
    recentHeadlinesCount?: number;
    sentiment?: string;
  } | null;
  portfolioImpact?: {
    currentCashWeightPct: number;
    expectedCashWeightPct: number;
    maxWeightBeforePct: number;
    maxWeightAfterPct: number;
    expectedVolatilityChangePct: number;
    riskSummary: string;
  } | null;
  riskRewardProfile?: {
    expectedReturnPct: number;
    maxRiskPct: number;
    riskRewardRatio: string;
    stopLossPolicy: string;
  } | null;
  reasoningSteps?: ReasoningStep[];
  createdAt: string;
}

export interface AgentTelemetryNode {
  worker_name: string;
  display_name: string;
  category: 'COLLECTOR' | 'NEWS' | 'QUANT' | 'AI_ANALYST' | 'SCHEDULER' | 'ORCHESTRATOR' | 'DISPATCHER' | 'EXECUTION';
  status: 'HEALTHY' | 'DEGRADED' | 'STALE' | 'IDLE';
  last_run_at: string;
  metrics_count?: number;
  summary_payload?: any;
}

export interface PipelineTopologyNode {
  id: string;
  name: string;
  category: 'INGESTION' | 'SCREENER' | 'RISK_FILTER' | 'DISPATCHER' | 'EXECUTION';
  status: 'HEALTHY' | 'DEGRADED' | 'STALE' | 'ACTIVE' | 'IDLE';
  icon: string;
  summary: string;
  latencyMs?: number;
  metrics: Record<string, any>;
}

export interface PipelineTopologyEdge {
  id: string;
  source: string;
  target: string;
  label: string;
  active: boolean;
  throughput?: number;
}

export interface PipelineTopologyData {
  success: boolean;
  mode: 'LIVE' | 'SANDBOX';
  timestamp: string;
  nodes: PipelineTopologyNode[];
  edges: PipelineTopologyEdge[];
}

export type MarketCode = 'KR' | 'US';

export type MarketSessionState = 
  | 'PRE_MARKET'   // 프리마켓 (미국 04:00~09:00, 한국 NXT 08:00~08:50 / KRX 08:30~08:40)
  | 'PRE_OPEN'     // 장 개장 직전 동시호가
  | 'MARKET_OPEN'  // 장 개장 직후 30분 (골든 아워)
  | 'REGULAR'      // 장중 정규 거래
  | 'PRE_CLOSE'    // 장 마감 전 동시호가
  | 'AFTER_MARKET' // 애프터마켓 (미국 16:00~20:00, 한국 KRX 시간외 15:40~18:00 / NXT 15:30~20:00)
  | 'DAY_MARKET'   // 미국 주간거래 (데이마켓 ATS - 미국 20:00~04:00, 한국 09:00~17:00 KST)
  | 'CLOSED';      // 장 마감 / 휴일

export interface KrExchangeSessionDetail {
  krxSession: string;
  nxtSession: string;
  canTradeKrx: boolean;
  canTradeNxt: boolean;
  activeExchanges: string[];
}

export interface UsExchangeSessionDetail {
  preMarket: string;
  preMarketKst: string;
  regularMarket: string;
  regularMarketKst: string;
  afterMarket: string;
  afterMarketKst: string;
  isDst: boolean;
  tzAbbr: string;
}

export interface MarketSessionInfo {
  market: MarketCode;
  displayName: string;
  timezone: string;
  session: MarketSessionState;
  isTradingAllowed: boolean;
  isExtendedHours?: boolean;
  isHoliday: boolean;
  isWeekend?: boolean;
  holidayName?: string;
  currentTimeLocal: string;
  regularOpenTime: string;
  regularCloseTime: string;
  nextOpenTimeUtc: string;
  minutesToOpen: number;
  minutesToClose: number;
  isDstActive?: boolean;
  krExchangeDetail?: KrExchangeSessionDetail;
  usExchangeDetail?: UsExchangeSessionDetail;
}

export interface UserTradingConfig {
  enabled: boolean;
  broker: string;
  budget: number;
  max_weight: number;
  stop_loss: number;
  mdd_breaker: number;
  daily_order_limit: number;
  budget_us?: number;
  daily_order_limit_us?: number;
  budget_kr?: number;
  daily_order_limit_kr?: number;
  paper_key?: string;
  paper_secret?: string;
  paper_account?: string;
  is_paper_trading?: boolean;
  order_execution_mode?: 'hitl' | 'auto';
  target_markets?: ('KR' | 'US')[];
  schedule_mode?: 'MARKET_HOURS_ONLY' | 'ALL_DAY';
  us_execution_session?: 'REGULAR_ONLY' | 'PRE_MARKET' | 'ALLOW_DAY_MARKET';
  max_spread_pct?: number;
  use_integrated_margin?: boolean;
  fee_rate_kr?: number | null;
  fee_rate_us?: number | null;
  simulation_mode?: 'hitl' | 'auto';
  paper_execution_mode?: 'VIRTUAL_SANDBOX' | 'BROKER_PAPER';
  virtual_seed_krw?: number;
  virtual_seed_usd?: number;
  paper_seed_krw?: number;
  paper_seed_usd?: number;
  take_profit?: number;
  paper_take_profit?: number;
  partial_profit_taking?: boolean;
  auto_rotation?: boolean;
  alert_channel_type?: 'DISCORD' | 'SLACK' | 'TELEGRAM';
  webhook_url?: string;
  telegram_chat_id?: string;
  updated_at?: string | null;
}

export interface AgentTask {
  id: string;
  userId: string;
  taskType: AgentTaskType;
  goal: string;
  status: AgentTaskStatus;
  proposedActions?: ProposedAction[] | null;
  userDecision?: 'APPROVED' | 'REJECTED' | 'AUTO_APPROVED' | 'SIMULATED' | null;
  rejectionReason?: string | null;
  tokensUsed?: number;
  confluenceScore?: number;
  strategySummary?: string | null;
  telemetrySnapshot?: AgentTelemetryNode[] | null;
  strategyProposal?: AgentStrategyProposal | null;
  marketMode?: 'US' | 'KR' | 'GLOBAL';
  executionMode?: 'PAPER' | 'LIVE';
  paperExecutionMode?: 'VIRTUAL_SANDBOX' | 'BROKER_PAPER';
  fallbackReason?: 'LIVE_KEY_REQUIRED' | 'MANUAL_LEDGER_SYNC' | 'USER_HITL_PREFERENCE' | 'INSUFFICIENT_CASH_HOLD' | string | null;
  createdAt: string;
  updatedAt: string;
  steps?: AgentTaskStep[];
}

export interface AgentTaskStep {
  id: string;
  taskId: string;
  stepNumber: number;
  state: 'PLANNING' | 'EXECUTING' | 'REFLECTING' | 'REASONING' | 'WAITING' | 'EXECUTED';
  thought?: string;
  actionTool?: string;
  actionInput?: string;
  observation?: string;
  createdAt: string;
}

// ─── 옵션 및 공매도 데이터 타입 정의 ───────────────────────────────────────────────
export interface OptionContract {
  optionSymbol: string;
  underlyingSymbol: string;
  expirationDate: string;
  strikePrice: number;
  optionType: 'CALL' | 'PUT';
  price: number;
  volume: number;
  openInterest: number;
  impliedVolatility?: number;
  delta?: number;
  gamma?: number;
  theta?: number;
  vega?: number;
}

export interface OptionChainData {
  symbol: string;
  asOfDate: string;
  underlyingPrice: number;
  expirations: string[];
  calls: OptionContract[];
  puts: OptionContract[];
}

export interface ShortInterestData {
  symbol: string;
  settlementDate: string;
  shortInterest: number;
  avgDailyVolume?: number;
  daysToCover?: number;
  shortPercentOfFloat?: number;
  costToBorrow?: number;
  floatShares?: number;
  updatedAt?: string;
}

export interface ShortVolumeData {
  symbol: string;
  tradeDate: string;
  shortVolume: number;
  shortExemptVolume?: number;
  totalVolume: number;
  shortRatio: number; // shortVolume / totalVolume
}

export interface QuantInsightsData {
  options: {
    pcrVolume: number;
    pcrOI: number;
    maxPain: number;
    maxPainDivergence: number;
    netGex: number;
    callGex: number;
    putGex: number;
    gexRegime: 'LONG_GAMMA' | 'SHORT_GAMMA';
  };
  shorts: {
    shortPercentOfFloat: number;
    costToBorrow: number;
    darkPoolShortRatio: number;
    avg5dDarkPoolShortRatio: number;
    shortExemptVolume: number;
    daysToCover: number;
  };
  composite: {
    gammaSqueezeScore: number;
    gammaSqueezeRisk: 'LOW' | 'MODERATE' | 'HIGH' | 'EXTREME';
    shortPressureIndex: number;
    shortPressureStatus: 'EXTREME_SQUEEZE_RISK' | 'HIGH_SQUEEZE_PRESSURE' | 'MODERATE_PRESSURE' | 'STABLE_LIQUIDITY' | 'BEARISH_HEAVY_SHORT';
  };
}

export interface IVRankData {
  symbol: string;
  currentIV: number;
  iv52WeekLow: number;
  iv52WeekHigh: number;
  ivRank: number; // 0 ~ 100
  ivPercentile: number; // 0 ~ 100
  updatedAt: string;
}

export interface MarketOptionsShortSentiment {
  market: 'US' | 'KR';
  tradeDate: string;
  isCached?: boolean;
  optionsSentiment: {
    putCallRatio: number;
    equityPcRatio: number;
    indexPcRatio: number;
    vix: number;
    vix9d?: number;
    vix3m?: number;
    vixTermStructureRatio: number;
    termStructureState: 'CONTANGO' | 'BACKWARDATION';
    skewIndex: number;
    marketGex: number;
    gammaRegime: 'LONG_GAMMA' | 'SHORT_GAMMA' | 'NEUTRAL';
    gammaFlipLevel?: number;
  };
  shortSentiment: {
    marketShortVolumeRatio: number;
    darkPoolShortRatio: number;
    highShortBasketReturn?: number;
    highShortBasketAvgCtb?: number;
    shortSqueezeRiskLevel: 'LOW' | 'MODERATE' | 'HIGH' | 'EXTREME';
  };
  marketRegime: {
    score: number;
    status: 'SQUEEZE_ALERT' | 'BEARISH_HEAVY' | 'NEUTRAL' | 'BULLISH_EXPANSION' | 'OVERBOUGHT_COMPLACENCY';
    summaryKo: string;
    summaryEn: string;
  };
  marketRadar: {
    gammaSqueezeCandidates: Array<{
      symbol: string;
      callVolumeRatio: number;
      atmCallOI: number;
      reason: string;
    }>;
    shortSqueezeCandidates: Array<{
      symbol: string;
      shortPercentOfFloat: number;
      daysToCover: number;
      costToBorrow: number;
      reason: string;
    }>;
    highIvSpike: Array<{
      symbol: string;
      ivRank: number;
      iv: number;
      reason: string;
    }>;
  };
}

// ─── Sector Credit Risk & DTS (Duration Times Spread) ───────────────────────

export interface SectorCreditDTSData {
  id?: string;
  sector: string;
  sectorNameKr: string;
  market: 'US' | 'KR';
  creditSpreadBp: number;
  spreadDuration: number;
  dtsValue: number;
  dtsRegime: 'LOW' | 'NORMAL' | 'ELEVATED' | 'HIGH_STRESS';
  benchmarkPercentile: number;
  dataSource: string;
  updatedAt?: string;
}

export interface DTSCompanyStressResult {
  sectorDTS: SectorCreditDTSData;
  companyDefenseGrade: 'RESILIENT' | 'MODERATE' | 'VULNERABLE' | 'CRITICAL';
  companyDefenseScore: number; // 0 ~ 100
  refinancingRisk: 'LOW' | 'MODERATE' | 'HIGH' | 'CRITICAL';
  spreadSensitivityPct: number;
  summaryDiagnosis: string;
  actionableAdvice: string;
}

// ─── Strategic Allocation & Simulator Integration ─────────────────────────────

export type SimulationMode = 'regime' | 'ai' | 'risk_slider';

export interface StrategicSimulatorTabConfig {
  initialTab?: 'strategy' | 'simulator';
  initialMode?: SimulationMode;
}



